import { getProductsFilterSchema } from "@dransay/api/models";
import { parseAsJson } from "nuqs";
import type { FiltersCategory, FiltersState } from "./types";
import { createInitialFilters } from "./use-filters.hook";

export const filterSchemaParser = parseAsJson(getProductsFilterSchema().parse);

export const getActiveFilterGroupsCount = (filters: FiltersState): number => {
	const defaultFilters = createInitialFilters();
	return Object.entries(filters).filter(
		([key, value]) =>
			key !== "topProducersLowPrice" &&
			isFilterActive(value, defaultFilters[key as keyof FiltersState]),
	).length;
};

// The exclusive row pin is suppressed while the user has narrowed the list
// down, but "Top producers" is a preset the app applies by default
// (`routes/helpers/get-products.ts` redirects the first visit to
// `filters={topProducersLowPrice:true}`), not a choice the user made, so it
// must not suppress the pin (MKP-2144). The preset also expands into a regular
// `producers` list in `products-layout.component.tsx`, which is why the key
// alone is not enough to recognise it. The two cannot disagree: editing the
// producer list clears `topProducersLowPrice` first
// (`sections/producer-filter.component.tsx`), so under the preset `producers`
// is always exactly its expansion.
//
// This is deliberately separate from `getActiveFilterGroupsCount`, which also
// drives the filter badge and the "X of Y" denominator and must keep counting
// the preset.
export const hasFiltersBeyondTopProducersPreset = (
	filters: FiltersState,
): boolean => {
	const defaultFilters = createInitialFilters();
	const isPreset = filters.topProducersLowPrice === true;

	return Object.entries(filters).some(
		([key, value]) =>
			key !== "topProducersLowPrice" &&
			!(isPreset && key === "producers") &&
			isFilterActive(value, defaultFilters[key as keyof FiltersState]),
	);
};

export const isFilterActive = (
	value: unknown,
	defaultValue: unknown,
): boolean => {
	if (Array.isArray(value) && Array.isArray(defaultValue)) {
		return value.length > 0;
	}

	return value !== undefined && value !== defaultValue;
};

export function mapFiltersByCategories(filters: FiltersState) {
	return Object.entries(filters) as Array<
		[FiltersCategory, FiltersState[FiltersCategory]]
	>;
}
